// The Offer tab (lane B8, S12; DC-202, CR-032, CR-033, CR-034, CR-036, CR-009): the offer's stage bar with the days
// on each step, the Offer Negotiation card with its dated facts and its Record actions, and the History — a log, never
// an edit surface. Every action is the server's to allow: a button is drawn only when offer.open's `can` and the
// session's tool manifest both admit it, so the Hiring Manager sees the facts and no Record button, and the
// Coordinator sees the chain and the dates and no note.
//  - Revise (his *Revised Offer*): the Recruiter only, on a negotiating offer; it proposes the revised package in
//    answer to the latest counter (comp.propose with responds_to_negotiation_id) and opens the Compensation tab.
//  - Decline: the reason and the next path in one dialog (offer.record_decline, DC-203).
//  - Withdrawn: candidate.withdraw (B3), then the next path (offer.choose_next_path).
//  - Schedule · Offer call: drawn only where the scheduling requests exist (B5) and the seat may ask for one.

import { useState } from "react";
import { businessDayISO, callTool, newIdempotencyKey } from "@/api/runtime";
import { errorText } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import {
  ACTION_LABELS, Button, CORE_LABELS, ErrorStrip, History, OFFER_STATUS, SCREEN_STATUS, SHELL_PAGE_LABELS, Stages, type HistoryEvent, type StagesItem,
  type Tone,
} from "@/mc";
import {
  DeclineDialog, NextPathDialog, RecordDialog, type DeclineInput, type NextPathInput, type RecordDraft, type RecordKind,
} from "./DecisionDialog";
import { OFFER_TAB_LABELS } from "./labels";
import { NegotiationCard } from "./NegotiationCard";
import { seam, type OfferTabProps, type OfferView, type Roles } from "./types";
import { WithdrawalDialog, type WithdrawalInput } from "./WithdrawalDialog";

// ---- the stage bar ------------------------------------------------------------------------------------------------------

/** The display chain's keys (D7) in his offer-chain words. */
const CHAIN_WORDS: Readonly<Record<string, string>> = {
  approved: OFFER_STATUS.approved,
  verbal_extended: OFFER_STATUS.verbalExtended,
  written_sent: OFFER_STATUS.writtenSent,
  awaiting_candidate: OFFER_STATUS.awaitingCandidate,
  negotiation: OFFER_STATUS.negotiation,
  revised_offer: OFFER_STATUS.revisedOffer,
  accepted: OFFER_STATUS.accepted,
  declined: OFFER_STATUS.declined,
};

const CHAIN_STATE = { done: "done", current: "active", pending: "next" } as const;

/** The chain as the stage bar draws it: each step's word, its state and the days spent on it. Unknown keys are left
 *  out rather than drawn as an id. */
export function chainItems(offer: OfferView): StagesItem[] {
  return (offer.chain?.steps ?? [])
    .filter((step) => CHAIN_WORDS[step.key] !== undefined)
    .map((step) => ({
      label: CHAIN_WORDS[step.key] as string,
      state: CHAIN_STATE[step.state],
      days: step.days ?? undefined,
    }));
}

// ---- the history -----------------------------------------------------------------------------------------------------------

const EVENT_WORDS: Readonly<Record<string, { word: string; tone: Tone }>> = {
  comp_recorded: { word: OFFER_STATUS.approved, tone: "info" },
  verbal_extended: { word: OFFER_STATUS.verbalExtended, tone: "info" },
  letter_sent: { word: OFFER_STATUS.writtenSent, tone: "info" },
  negotiating: { word: OFFER_STATUS.negotiation, tone: "warn" },
  comp_revised: { word: OFFER_STATUS.revisedOffer, tone: "warn" },
  accepted: { word: OFFER_STATUS.accepted, tone: "ok" },
  declined: { word: OFFER_STATUS.declined, tone: "red" },
  withdrawn: { word: SCREEN_STATUS.withdrawn, tone: "neutral" },
  "comp.reopen": { word: ACTION_LABELS.revise, tone: "warn" },
};

/** The offer's events as History rows: the word, who, when. Read-only by construction — History has no edit form. */
export function historyEvents(offer: OfferView): HistoryEvent[] {
  return offer.events
    .map((event, index) => {
      const kind = String(event.kind ?? "");
      const known = EVENT_WORDS[kind];
      const by = typeof event.by === "string" && event.by ? { name: event.by } : undefined;
      return {
        id: `${kind}-${index}`,
        actor: by,
        sentence: known?.word ?? CORE_LABELS.recordedAuditEvent,
        at: String(event.at ?? ""),
        tone: known?.tone ?? "neutral",
      } satisfies HistoryEvent;
    })
    .filter((event) => event.at !== "");
}

// ---- what this seat may do ------------------------------------------------------------------------------------------------

export interface OfferActs {
  verbal: boolean;
  written: boolean;
  counter: boolean;
  revise: boolean;
  accept: boolean;
  decline: boolean;
  withdraw: boolean;
  offerCall: boolean;
}

/** The server's `can` flags AND the session's manifest; the Revised Offer also needs the Recruiter on a negotiating
 *  offer. */
export function actsFor(offer: OfferView, roles: Roles, toolCan: (tool: string) => boolean): OfferActs {
  const can = offer.can ?? {};
  const both = (flag: string, tool: string) => can[flag] === true && toolCan(tool);
  return {
    verbal: both("record_verbal", "offer.record_verbal"),
    written: both("record_letter_sent", "offer.record_letter_sent"),
    counter: both("record_response", "offer.record_response"),
    revise: offer.status === "negotiating" && roles.includes("recruiter") && toolCan("comp.propose") && can.revise !== false,
    accept: both("record_acceptance", "offer.record_acceptance"),
    decline: both("record_decline", "offer.record_decline"),
    withdraw: both("withdraw", "candidate.withdraw"),
    // REBASE: B5 — the flag exists only once the scheduling requests have landed
    offerCall: both("request_offer_call", "offer.request_offer_call"),
  };
}

// ---- the pure view (server-rendered in the tests) -----------------------------------------------------------------------

export type OfferAction = RecordKind | "revise" | "decline" | "withdraw" | "offerCall";

export interface OfferTabViewProps {
  offer: OfferView;
  roles: Roles;
  toolCan: (tool: string) => boolean;
  onAct?: ((action: OfferAction) => void) | undefined;
  busy?: OfferAction | null | undefined;
}

export function OfferTabView({ offer, roles, toolCan, onAct, busy }: OfferTabViewProps) {
  const acts = actsFor(offer, roles, toolCan);
  const act = (action: OfferAction) => () => onAct?.(action);
  const buttons = (
    <div className="mc-offer-acts">
      {acts.verbal ? <Button sm  onClick={act("verbal")}>{ACTION_LABELS.extendVerbalOffer}</Button> : null}
      {acts.written ? <Button sm  onClick={act("written")}>{ACTION_LABELS.sendWritten}</Button> : null}
      {acts.counter ? <Button sm  onClick={act("counter")}>{ACTION_LABELS.counter}</Button> : null}
      {acts.revise ? <Button sm  busy={busy === "revise"} onClick={act("revise")}>{ACTION_LABELS.revise}</Button> : null}
      {acts.accept ? <Button sm tone="primary"  onClick={act("accept")}>{ACTION_LABELS.accept}</Button> : null}
      {acts.decline ? <Button sm  onClick={act("decline")}>{ACTION_LABELS.decline}</Button> : null}
      {acts.withdraw ? <Button sm  onClick={act("withdraw")}>{SCREEN_STATUS.withdrawn}</Button> : null}
      {acts.offerCall ? (
        <Button sm  busy={busy === "offerCall"} onClick={act("offerCall")}>
          {ACTION_LABELS.schedule}{" · "}{OFFER_TAB_LABELS.offerCall}
        </Button>
      ) : null}
    </div>
  );
  return (
    <div className="mc-offer-tab">
      <Stages items={chainItems(offer)} label={SHELL_PAGE_LABELS.offer} />
      <NegotiationCard offer={offer} roles={roles} actions={buttons} />
      <History events={historyEvents(offer)} label={SHELL_PAGE_LABELS.history} />
    </div>
  );
}

// ---- the wired tab --------------------------------------------------------------------------------------------------------

const dayISO = businessDayISO;

type Open = RecordKind | "decline" | "withdraw" | "nextPath" | null;

export function OfferTab({ requisitionId, applicationId, offer: raw, reload }: OfferTabProps) {
  const { me, can } = useSession();
  const offer = seam(raw);
  const roles = me?.roles ?? [];
  const [open, setOpen] = useState<Open>(null);
  const [busy, setBusy] = useState<OfferAction | "submit" | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const ref = { requisition_id: requisitionId, application_id: applicationId };

  const send = async (tool: string, input: Record<string, unknown>, after: Open = null) => {
    setBusy("submit");
    setRefused(null);
    try {
      await callTool(tool, { ...ref, ...input }, { idempotencyKey: newIdempotencyKey() });
      setOpen(after);
      reload();
    } catch (error) {
      setRefused(errorText(error));
    } finally {
      setBusy(null);
    }
  };

  const record = (kind: RecordKind) => (draft: RecordDraft) => {
    if (kind === "verbal") void send("offer.record_verbal", { extended_at: dayISO(draft.date), terms_conveyed: draft.note.trim() });
    if (kind === "written") void send("offer.record_letter_sent", { letter_ref: draft.text.trim(), sent_at: dayISO(draft.date) });
    if (kind === "accept") void send("offer.record_acceptance", { accepted_at: dayISO(draft.date) });
    // REBASE: S07 — the counter's field list; one free-text request until the dialog carries the fields. How the
    // request reached the Recruiter has no word of his yet, so the round says `other` until the dialog asks
    if (kind === "counter") {
      void send("offer.record_response", {
        response: "negotiating",
        counter: [{ field: "other", text: draft.text.trim() }],
        source: "other",
        candidate_note: draft.note.trim() || null,
      });
    }
  };

  const decline = (input: DeclineInput) => void send("offer.record_decline", { ...input });
  const withdraw = (input: WithdrawalInput) =>
    void send("candidate.withdraw", { reason_code: input.reason_code, reason_text: input.reason_text, effective_at: dayISO(input.date) }, "nextPath");
  const nextPath = (input: NextPathInput) => void send("offer.choose_next_path", { ...input });

  const act = async (action: OfferAction) => {
    setRefused(null);
    if (action === "revise") {
      const pkg = (offer.package ?? {}) as Record<string, unknown>;
      setBusy("revise");
      try {
        await callTool("comp.propose", {
          ...ref,
          base: pkg.base,
          currency: pkg.currency,
          start_date: pkg.start_date,
        }, { idempotencyKey: newIdempotencyKey() });
        reload();
        window.location.hash = `#requisitions/${requisitionId}/offer/${applicationId}?tab=compensation`;
      } catch (error) {
        setRefused(errorText(error));
      } finally {
        setBusy(null);
      }
      return;
    }
    if (action === "offerCall") {
      setBusy("offerCall");
      try {
        await callTool("offer.request_offer_call", ref, { idempotencyKey: newIdempotencyKey() });
        reload();
      } catch (error) {
        setRefused(errorText(error));
      } finally {
        setBusy(null);
      }
      return;
    }
    setOpen(action);
  };

  const close = () => {
    setOpen(null);
    setRefused(null);
  };
  const shell = { onClose: close, busy: busy === "submit", refused };
  const stage = offer.stage === "offer" ? SHELL_PAGE_LABELS.offer : offer.stage;
  return (
    <>
      {refused && open === null ? <ErrorStrip message={refused} /> : null}
      <OfferTabView offer={offer} roles={roles} toolCan={can} onAct={(action) => void act(action)} busy={busy === "submit" ? null : busy} />
      {open === "verbal" || open === "written" || open === "counter" || open === "accept" ? (
        <RecordDialog key={open} kind={open} open {...shell} onSubmit={record(open)} />
      ) : null}
      {open === "decline" ? (
        <DeclineDialog open {...shell} reasons={offer.decline_reasons} categories={offer.reporting_categories} alternates={offer.alternates} onSubmit={decline} />
      ) : null}
      {open === "withdraw" ? (
        <WithdrawalDialog open {...shell} reasons={offer.withdrawal_reasons} stage={stage} onSubmit={withdraw} />
      ) : null}
      {open === "nextPath" ? (
        <NextPathDialog open {...shell} categories={offer.reporting_categories} alternates={offer.alternates} onSubmit={nextPath} />
      ) : null}
    </>
  );
}
